import React, { useEffect, useState } from 'react'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { getTimeseries } from '../core/api'
import { nf } from '../core/consts'
import { Granularity, TimeseriesBucket } from '../core/types'
import { METRIC_HINTS } from '../core/hints'
import { useChartTheme } from '../core/chartTheme'
import { SERIES_COLOR } from '../core/seriesColors'
import { hourlyAxis, dayDividerEvents, HourlyAxis } from '../core/timeAxis'
import { parseServerInstant } from '../core/mskTime'
import InfoHint from '../../../../../common/InfoHint'

// Daily buckets already carry the date and never need a divider; hourly ones get
// the date on a second line at every MSK day change (see hourlyAxis).
function axisFor(buckets: TimeseriesBucket[], granularity: Granularity): HourlyAxis {
  if (granularity === 'hour') return hourlyAxis(buckets.map((b) => b.ts))
  return {
    categories: buckets.map((b) =>
      parseServerInstant(b.ts).toLocaleDateString('ru-RU', {
        timeZone: 'Europe/Moscow',
        day: '2-digit',
        month: '2-digit',
      })
    ),
    dayStarts: [],
  }
}

const TimelineChart: React.FC<{ currency: string }> = ({ currency }) => {
  const [granularity, setGranularity] = useState<Granularity>('hour')
  const [buckets, setBuckets] = useState<TimeseriesBucket[]>([])
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const theme = useChartTheme()

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    getTimeseries(granularity, currency)
      .then((resp) => {
        if (cancelled) return
        if (resp.data.success) {
          setBuckets(resp.data.data.buckets ?? [])
          setError(null)
        } else {
          setError('Не удалось загрузить таймлайн')
        }
      })
      .catch(() => !cancelled && setError('Сервис мэтчинга недоступен'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [granularity, currency])

  // Period totals for the legend.
  const depSum = buckets.reduce((s, b) => s + b.deposits_amount, 0)
  const paySum = buckets.reduce((s, b) => s + b.payouts_amount, 0)
  const created = buckets.reduce((s, b) => s + b.matching_created, 0)
  const completed = buckets.reduce((s, b) => s + b.matching_completed, 0)
  const overallConv = created > 0 ? Math.round((completed / created) * 100) : 0

  const series = [
    { name: 'Депозиты', type: 'column', data: buckets.map((b) => b.deposits_amount) },
    { name: 'Выплаты', type: 'column', data: buckets.map((b) => b.payouts_amount) },
    {
      name: 'Конверсия закрытия выплат',
      type: 'line',
      data: buckets.map((b) => Math.round(b.conversion_pct)),
    },
  ]

  const axis = axisFor(buckets, granularity)

  const options: ApexOptions = {
    chart: {
      type: 'line',
      toolbar: { show: false },
      animations: { enabled: false },
      background: 'transparent',
      events: dayDividerEvents(axis.dayStarts, theme.dividerColor),
    },
    theme: { mode: theme.mode },
    // Порядок серий: депозиты, выплаты, конверсия. Литералы заменены на общее
    // правило дашборда, чтобы эталонный график не разъехался при следующей правке.
    colors: [SERIES_COLOR.deposit, SERIES_COLOR.payout, '#7239EA'],
    stroke: { width: [0, 0, 3], curve: 'straight' },
    plotOptions: { bar: { columnWidth: '60%', borderRadius: 3 } },
    dataLabels: { enabled: false },
    grid: { borderColor: theme.gridColor },
    // Тултип оси у Apex полупрозрачный и наезжает на подпись под ним; час и так
    // есть в заголовке основного тултипа.
    xaxis: { categories: axis.categories, tooltip: { enabled: false } },
    yaxis: [
      { seriesName: 'Депозиты', title: { text: 'Объём' }, min: 0 },
      { seriesName: 'Выплаты', show: false, min: 0 },
      {
        seriesName: 'Конверсия закрытия выплат',
        opposite: true,
        max: 100,
        min: 0,
        title: { text: 'Конверсия %' },
      },
    ],
    legend: {
      position: 'top',
      formatter: (name: string) => {
        if (name === 'Депозиты') return `Депозиты (Σ ${nf.format(Math.round(depSum))})`
        if (name === 'Выплаты') return `Выплаты (Σ ${nf.format(Math.round(paySum))})`
        if (name === 'Конверсия закрытия выплат')
          return `Конверсия закрытия выплат (${overallConv}% за период)`
        return name
      },
    },
    tooltip: { shared: true, intersect: false },
  }

  return (
    <div className='card mb-5'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <h3 className='card-title d-inline-flex align-items-center'>
          Динамика депозитов, выплат и конверсии
          <InfoHint text={METRIC_HINTS.timeline} />
        </h3>
        <div className='btn-group btn-group-sm'>
          <button
            className={`btn btn-sm ${granularity === 'hour' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setGranularity('hour')}
            title='Последние 24 часа по часам'
          >
            День
          </button>
          <button
            className={`btn btn-sm ${granularity === 'day' ? 'btn-primary' : 'btn-light'}`}
            onClick={() => setGranularity('day')}
            title='Последние 30 дней по дням'
          >
            Месяц
          </button>
        </div>
      </div>
      <div className='card-body'>
        {error ? (
          <div className='text-danger'>{error}</div>
        ) : (
          <div className='position-relative'>
            <Chart options={options} series={series} type='line' height={340} />
            {loading && (
              <div
                className='position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center'
                style={{ background: theme.overlayBg, zIndex: 5 }}
              >
                <span className='spinner-border text-primary' role='status' />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default TimelineChart
